import { useEffect, useState, type FormEvent } from "react";
import { Alert, Button, PasswordInput, Stack, Text, Title } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { useHostedSession } from "../hostedSession";
import { accountsApi } from "./api";
import { roleLabel, type InvitationPreview } from "./types";
import "./accounts.css";

export function InvitationActivationPage() {
  const [token, setToken] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get("token") || "");
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { session, logout, refresh } = useHostedSession();
  const cache = useQueryClient();
  const navigate = useNavigate();
  useEffect(() => {
    const capture = () => {
      const received = new URLSearchParams(window.location.hash.slice(1)).get("token");
      if (received) {
        setToken(received);
        window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
      }
    };
    capture();
    window.addEventListener("hashchange", capture);
    return () => window.removeEventListener("hashchange", capture);
  }, []);
  useEffect(() => {
    setPreview(null); setError("");
    if (!token) return;
    let active = true;
    accountsApi.inspect(token).then(value => { if (active) setPreview(value); }).catch(reason => { if (active) setError(reason.message); });
    return () => { active = false; };
  }, [token]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || !preview) return;
    if (!preview.account_exists && password !== confirmation) { setError("Les mots de passe diffèrent."); return; }
    setBusy(true); setError("");
    try {
      await accountsApi.accept(token, password);
      cache.removeQueries({ predicate: query => query.queryKey[0] !== "hosted-session" });
      await refresh();
      navigate("/events", { replace: true });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Impossible d’activer cet accès.");
      setPassword(""); setConfirmation("");
      try { setPreview(await accountsApi.inspect(token)); } catch { /* Keep the actionable acceptance error. */ }
    } finally { setBusy(false); }
  }
  const otherAccount = preview && session && session.user.email !== preview.email;
  return <main className="hosted-auth-shell"><section className="hosted-auth-card accounts-activation">
    <Stack gap="lg">
      <div><p className="hosted-kicker">Invitation Relyo</p><Title order={1}>Rejoindre votre entreprise</Title></div>
      {!token ? <Alert color="yellow">Rouvrez le lien d’invitation qui vous a été transmis pour continuer.</Alert> : null}
      {error ? <Alert color="red" role="alert">{error}</Alert> : null}
      {token && !preview && !error ? <Text>Vérification de l’invitation…</Text> : null}
      {preview ? <>
        <div className="accounts-invitation-summary"><strong>{preview.tenant_name}</strong><Text>{preview.email}</Text><Text size="sm">{roleLabel(preview.role)} · Invitation valable jusqu’au {new Date(preview.expires_at).toLocaleDateString("fr-FR")}</Text></div>
        {otherAccount ? <><Alert color="yellow">Un autre compte est connecté. Déconnectez-vous pour accepter cette invitation.</Alert><Button variant="light" onClick={async () => { try { await logout(); } catch (e) { setError((e as Error).message); } }}>Se déconnecter</Button></> :
        <form onSubmit={submit}><Stack>
          <PasswordInput required label={preview.account_exists ? "Votre mot de passe actuel" : "Choisir un mot de passe"} value={password} onChange={e => setPassword(e.currentTarget.value)} minLength={preview.account_exists ? 1 : 8} maxLength={1024} autoComplete={preview.account_exists ? "current-password" : "new-password"} description={preview.account_exists ? "Votre compte existe déjà : son mot de passe reste inchangé." : "Au moins 8 caractères."} />
          {!preview.account_exists ? <PasswordInput required label="Confirmer le mot de passe" value={confirmation} onChange={e => setConfirmation(e.currentTarget.value)} autoComplete="new-password" /> : null}
          <Button type="submit" loading={busy}>Activer mon compte</Button>
        </Stack></form>}
      </> : null}
      <Link to="/login">Retour à la connexion</Link>
    </Stack>
  </section></main>;
}
